import vert from '!raw-loader!./vertex.glsl';
import frag from '!raw-loader!./fragment.glsl';
import {reportNonFatalError} from '../../../misc/errorReporting/errorReporter';

function loadTexture(gl, oc) {
  const texture = gl.createTexture();
  if (!texture) {
    return null;
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, oc); // This is the important line!
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
}

function setArrays(gl, prog) {
  const vertexBuffer = gl.createBuffer();
  if (!vertexBuffer) {
    return null;
  }
  const vertices = [-1.0, -1.0, 1.0, -1.0, -1.0, 1.0,
    1.0, 1.0, 1.0, -1.0, -1.0, 1.0,
  ];
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
  const coord = gl.getAttribLocation(prog, "coords");
  gl.vertexAttribPointer(coord, 2, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(coord);
  return vertexBuffer;
}

function compileShader(gl, type, source) {
  // createShader returns null when the context is lost (e.g. too many live
  // WebGL contexts in Safari, GPU reset, tab backgrounded on iOS).
  const shader = gl.createShader(type);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Canvas enhancement shader failed to compile: ${log}`);
  }
  return shader;
}

function prepareWebGL(gl, width, height) {
  const vertSh = compileShader(gl, gl.VERTEX_SHADER, vert);
  const fragSh = vertSh && compileShader(gl, gl.FRAGMENT_SHADER, frag);
  const prog = fragSh && gl.createProgram();
  if (!prog) {
    return null;
  }
  gl.attachShader(prog, vertSh);
  gl.attachShader(prog, fragSh);
  gl.linkProgram(prog);
  // Shaders are no longer needed once linked into the program.
  gl.deleteShader(vertSh);
  gl.deleteShader(fragSh);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS) && !gl.isContextLost()) {
    const log = gl.getProgramInfoLog(prog);
    gl.deleteProgram(prog);
    throw new Error(`Canvas enhancement program failed to link: ${log}`);
  }
  gl.useProgram(prog);
  gl.viewport(0, 0, width, height);
  return prog;
}

// Creates a canvas with a ready-to-draw WebGL pipeline, or null if WebGL is
// unavailable / the context is lost.
function createWebGLTarget(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
  if (!gl || gl.isContextLost()) {
    return null;
  }
  const prog = prepareWebGL(gl, width, height);
  if (!prog || !setArrays(gl, prog)) {
    releaseWebGLTarget({canvas, gl});
    return null;
  }
  gl.uniform1i(gl.getUniformLocation(prog, 'uSampler'), 0);
  return {canvas, gl};
}

// Frees the GPU context right away instead of waiting for GC. Browsers cap the
// number of live WebGL contexts (Safari is especially strict) and silently
// lose the oldest one, which then breaks the shared context below.
function releaseWebGLTarget(target) {
  if (!target || !target.gl) {
    return;
  }
  const loseContext = target.gl.getExtension && target.gl.getExtension('WEBGL_lose_context');
  if (loseContext) {
    loseContext.loseContext();
  }
  target.canvas.width = 0;
  target.canvas.height = 0;
}

let sharedTarget = null;
let hasEnhancementFailed = false;

function getSharedTarget(width, height) {
  if (sharedTarget && sharedTarget.gl.isContextLost()) {
    // A lost context can't be revived on the same canvas: start over.
    sharedTarget = null;
  }
  if (!sharedTarget) {
    sharedTarget = createWebGLTarget(width, height);
  } else if (sharedTarget.canvas.width !== width || sharedTarget.canvas.height !== height) {
    sharedTarget.canvas.width = width;
    sharedTarget.canvas.height = height;
    sharedTarget.gl.viewport(0, 0, width, height);
  }
  return sharedTarget;
}

// Returns true when the enhanced image was drawn back onto canvasElement.
function drawEnhanced(target, canvasElement) {
  const {gl, canvas} = target;
  const texture = loadTexture(gl, canvasElement);
  if (!texture) {
    return false;
  }
  try {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (gl.isContextLost()) {
      // Copying now would paint a blank/black image over the original.
      return false;
    }
    canvasElement.getContext('2d').drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, canvasElement.width, canvasElement.height);
    return true;
  } finally {
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.deleteTexture(texture);
  }
}

const renderEnhancedCanvas = (canvasElement, useNewCanvas = false) => {
  const {width, height} = canvasElement;
  if (!width || !height) {
    return;
  }

  if (useNewCanvas) {
    const target = createWebGLTarget(width, height);
    if (!target) {
      return;
    }
    try {
      drawEnhanced(target, canvasElement);
    } finally {
      releaseWebGLTarget(target);
    }
    return;
  }

  const target = getSharedTarget(width, height);
  if (target) {
    drawEnhanced(target, canvasElement);
  }
};

// Enhancement is optional: leave the original 2D canvas intact if WebGL fails.
// A lost context is not treated as a failure; the next call simply retries.
const enhanceCanvas = (canvasElement, useNewCanvas = false) => {
  if (hasEnhancementFailed) {
    return;
  }

  try {
    renderEnhancedCanvas(canvasElement, useNewCanvas);
  } catch (error) {
    // A partially initialized context must not be reused on subsequent redraws.
    hasEnhancementFailed = true;
    if (sharedTarget) {
      releaseWebGLTarget(sharedTarget);
      sharedTarget = null;
    }
    reportNonFatalError('canvas-enhancement-failed', error);
  }
};

export default enhanceCanvas
